// src/components/Customers.tsx
import React, { useState, useEffect, useRef } from 'react';
import { PiBuildingsBold, PiNotePencilBold } from 'react-icons/pi';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { FaRegFileAlt, FaBuilding, FaUser, FaEnvelope, FaPhone } from 'react-icons/fa';
import { SlGraph } from 'react-icons/sl';
import { TbNotes, TbEdit } from 'react-icons/tb';
import { GoHash } from 'react-icons/go';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { GrLocation } from 'react-icons/gr';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import Button from '../components/ui/Button/Button';
import { IoAddOutline } from 'react-icons/io5';
import { MdOutlineGroups, MdLockOutline } from 'react-icons/md';
import TableSearchFilter from '../components/utils/TableSearchFilter';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchCustomers, searchCustomers } from '../redux/actions/customerActions';
import { clearSearchResults } from '../redux/slices/customerSlice';
import toast from 'react-hot-toast';
import Loading from '../components/utils/Loading';
import PopupWrapper from '../components/utils/PopupWrapper';
import { usePopup } from '../hooks/usePopup';
import InputField from '../components/ui/InputField';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get customer data from Redux
  const { loading, error, pagination, searchResults } = useAppSelector(state => state.customers);

  // Get user data from auth state
  const { user } = useAppSelector(state => state.auth);

  // State for permissions, search, and customers
  const [userPermissions, setUserPermissions] = useState({
    customerRead: false,
    customerWrite: false,
    customerEdit: false,
  });
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Customer details popup state
  const customerDetailsPopup = usePopup();
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

  // Use a ref to track if we've already shown the permission error
  const permissionErrorShown = useRef(false);

  // Use ref for timeout to properly handle debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle search input change with debounce
  const handleSearchChange = (query: string) => {
    console.log('Search query changed:', query);
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length === 0) {
      console.log('Query is empty, clearing search results');
      dispatch(clearSearchResults());
      setIsSearching(false);
      // Reset to page 1 when clearing search
      setCurrentPage(1);
      return;
    }

    setIsSearching(true);
    console.log('Setting search in progress...');

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      console.log('Executing search for:', query.trim());
      // Reset to page 1 when searching
      setCurrentPage(1);
      dispatch(searchCustomers(query.trim()))
        .unwrap()
        .then(result => {
          console.log('Search results received:', result);
        })
        .catch(error => {
          console.error('Search error:', error);
          toast.error('Error searching customers');
        })
        .finally(() => {
          setIsSearching(false);
        });
    }, 300);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Initialize permissions from user data
  useEffect(() => {
    if (user) {
      setPermissionsLoading(true);
      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setUserPermissions({
          customerRead: true,
          customerWrite: true,
          customerEdit: true,
        });
        setPermissionsLoading(false);
        return;
      }

      // For non-superAdmin users, check permissions
      if (user.permissions) {
        let customerReadValue = false;
        let customerWriteValue = false;
        let customerEditValue = false;

        if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          // If permissions is an array of objects
          const permObj = user.permissions[0];
          // Check for all possible permission naming conventions
          customerReadValue = Boolean(
            permObj.customerRead || permObj.readCustomer || permObj.getClient
          );
          customerWriteValue = Boolean(
            permObj.customerWrite || permObj.writeCustomer || permObj.creaateClient // Note: This matches the typo in the backend
          );
          customerEditValue = Boolean(
            permObj.customerEdit || permObj.editCustomer || permObj.updateClient
          );
        } else if (typeof user.permissions === 'object') {
          // If permissions is an object
          customerReadValue = Boolean(
            user.permissions.customerRead ||
            user.permissions.readCustomer ||
            user.permissions.getClient
          );
          customerWriteValue = Boolean(
            user.permissions.customerWrite ||
            user.permissions.writeCustomer ||
            user.permissions.creaateClient // Note: This matches the typo in the backend
          );
          customerEditValue = Boolean(
            user.permissions.customerEdit ||
            user.permissions.editCustomer ||
            user.permissions.updateClient
          );
        }

        setUserPermissions({
          customerRead: customerReadValue,
          customerWrite: customerWriteValue,
          customerEdit: customerEditValue,
        });
      }
      setPermissionsLoading(false);
    }
  }, [user]);

  // Separate useEffect for authentication check
  useEffect(() => {
    // Redirect if not authenticated
    if (user === null) {
      toast.error('Please log in to access this page', {
        duration: 3000,
        id: 'login-required', // Prevent duplicate toasts
      });
      navigate('/auth/login');
    }
  }, [user, navigate]);

  // Fetch customers after permissions are initialized
  useEffect(() => {
    // Only run this effect if user data is available and permissions are loaded
    if (!user || permissionsLoading) return;

    const hasAccess = userPermissions.customerRead || user?.designation === 'superAdmin';

    if (hasAccess) {
      dispatch(fetchCustomers({ page: currentPage, limit: itemsPerPage }))
        .unwrap()
        .catch(error => {
          console.error('Error fetching customers:', error);
        });
    } else if (!permissionErrorShown.current) {
      // Only show the permission error once
      permissionErrorShown.current = true;
      toast.error("You don't have permission to view customers", {
        duration: 3000,
        id: 'permission-error', // Prevent duplicate toasts
      });
    }
  }, [dispatch, userPermissions.customerRead, user, permissionsLoading, currentPage, itemsPerPage]);

  // Get customers from Redux
  const { customers } = useAppSelector(state => state.customers);
  const displayCustomers = searchQuery.trim() !== '' ? searchResults : customers;

  // Format customer status for display
  const formatCustomerStatus = (
    status: string
  ): { text: string; icon: JSX.Element; className?: string } => {
    if (!status)
      return {
        text: 'Unknown',
        icon: <IoMdCloseCircleOutline className="text-gray-500 text-3xl" />,
        className: 'bg-gray-100 text-gray-800',
      };

    if (status.toUpperCase() === 'ACTIVE') {
      return {
        text: 'Active',
        icon: (
          <span className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
            <FaRegCircleCheck className="text-green-500 text-2xl" />
          </span>
        ),
        className: 'bg-green-100 text-green-800',
      };
    }

    if (status.toUpperCase() === 'INACTIVE') {
      return {
        text: 'Inactive',
        icon: (
          <span className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
            <IoMdCloseCircleOutline className="text-red-500 text-3xl" />
          </span>
        ),
        className: 'bg-red-100 text-red-800',
      };
    }

    // Default case for any other status (yellow color)
    return {
      text: status,
      icon: (
        <span className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full">
          <IoMdCloseCircleOutline className="text-yellow-500 text-3xl" />
        </span>
      ),
      className: 'bg-yellow-100 text-yellow-800',
    };
  };

  // Handle edit customer
  const handleEdit = (customerId: number) => {
    // Check if user has permission to edit customers
    if (!userPermissions.customerEdit && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to edit customers", {
        duration: 3000,
      });
      return;
    }
    navigate(`/edit-customer/${customerId}`);
  };

  // Handle navigation to add customer page
  const handleAddCustomer = () => {
    // Check if user has permission to add customers
    if (!userPermissions.customerWrite && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to add customers", {
        duration: 3000,
      });
      return;
    }
    navigate('/add-customer');
  };

  // Handle customer row click to open details popup
  const handleRowClick = (customer: any) => {
    setSelectedCustomer(customer);
    customerDetailsPopup.openPopup();
  };

  // Define table columns with enhanced styling for visibility
  const columns = [
    {
      key: 'Id',
      header: 'Id',
      icon: <GoHash />,
      priority: 1,
      width: 'w-[8%]',
      headerClassName: 'font-bold',
      renderCell: (_: any, customer: any) => (
        <span className="flex justify-start items-center h-full w-full">{customer.cliendId}</span>
      ),
    },
    {
      key: 'companyName',
      header: 'Company Name',
      icon: <PiBuildingsBold />,
      priority: 5, // High priority to show on smaller screens
      width: 'w-[24%]',
      headerClassName: 'font-bold',
      renderCell: (_: any, customer: any) => {
        const name = customer.companyName || '-';
        return (
          <span
            className="flex justify-start items-center h-full w-full cursor-pointer"
            title={name}
          >
            {name.length > 20 ? `${name.slice(0, 20)}...` : name}
          </span>
        );
      },
    },
    {
      key: 'ownerName',
      header: 'Owner Name',
      icon: <FaRegFileAlt />,
      priority: 4,
      width: 'w-[20%]',
      headerClassName: 'font-bold',
      renderCell: (_: any, customer: any) => {
        const name = customer.ownerName || '-';
        return (
          <span
            className="flex justify-start items-center h-full w-full cursor-pointer"
            title={name}
          >
            {name.length > 30 ? `${name.slice(0, 30)}...` : name}
          </span>
        );
      },
    },
    {
      key: 'state',
      header: 'State',
      icon: <GrLocation />,
      priority: 3,
      width: 'w-[15%]',
      headerClassName: 'font-bold',
      renderCell: (_: any, customer: any) => {
        // Access state from clientAddress if available
        const state = customer.clientAddress?.state || '-';
        return (
          <span
            className="flex justify-start items-center h-full w-full cursor-pointer"
            title={state}
          >
            {state.length > 20 ? `${state.slice(0, 20)}...` : state}
          </span>
        );
      },
    },
    {
      key: 'customerStatus',
      header: 'Status',
      icon: <SlGraph />,
      priority: 2,
      width: 'w-[15%]',
      headerClassName: 'font-bold',
      cellClassName: 'text-center',
    },
    {
      key: 'action',
      header: 'Action',
      icon: <TbNotes />,
      priority: 6, // Highest priority to ensure it's always visible
      width: 'w-[10%]',
      headerClassName: 'font-bold text-red-500',
      cellClassName: 'text-center',
      renderCell: (_: any, customer: any) => (
        <button
          onClick={e => {
            e.stopPropagation();
            handleEdit(customer.id);
          }}
          className="cursor-pointer px-3 py-1 text-sm bg-white text-[#28A745] rounded-full border border-[#28A745] inline-flex items-center gap-1 hover:bg-[#28A745] hover:text-white transition-colors"
        >
          <PiNotePencilBold />
          Edit
        </button>
      ),
    },
  ];

  // Check if user has permission to view customers
  const hasReadAccess = userPermissions.customerRead || user?.designation === 'superAdmin';
  const hasWriteAccess = userPermissions.customerWrite || user?.designation === 'superAdmin';
  const hasEditAccess = userPermissions.customerEdit || user?.designation === 'superAdmin';

  if (permissionsLoading) {
    return (
      <ComponentWrapper icon={<MdOutlineGroups size={30} />} title="All Customers">
        <div className="flex justify-center items-center h-64">
          <Loading height="200px" />
        </div>
      </ComponentWrapper>
    );
  }

  // If user has no permission at all, show access denied message
  if (!hasReadAccess && user !== null) {
    return (
      <ComponentWrapper
        icon={<MdLockOutline size={24} />}
        title="Access Denied"
        className="min-h-[70vh] flex items-center justify-center"
      >
        <div className="text-center py-12 max-w-lg mx-auto">
          <div className="bg-red-100 text-red-600 p-4 rounded-full inline-flex mb-6">
            <MdLockOutline size={48} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Permission Required</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the Customers page. Please contact your
            administrator if you need access to this feature.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </ComponentWrapper>
    );
  }
  // {console.log(selectedCustomer.logo)}
  return (
    <ComponentWrapper
      icon={<MdOutlineGroups size={30} />}
      title="All Customers"
      buttonsTwo={[
        {
          component: hasWriteAccess ? (
            <Button
              variant="secondary"
              size="sm"
              className="py-2"
              icon={<IoAddOutline size={16} />}
              onClick={handleAddCustomer}
            >
              Add new customer
            </Button>
          ) : null,
        },
      ]}
    >
      <TableSearchFilter
        searchValue={searchQuery}
        searchPlaceholder="Search customers..."
        onSearchChange={handleSearchChange}
        showFilterButton={false}
      />

      <div className="bg-white mt-8 rounded-lg">
        <div className="mt-2">
          {loading ? (
            <div className="flex justify-center items-center ">
              <Loading height="400px" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">Error loading customers: {error}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <ResponsiveTable
                columns={hasEditAccess ? columns : columns.filter(col => col.key !== 'action')}
                data={displayCustomers}
                emptyText={searchQuery ? 'No customers match your search' : 'No customers found'}
                isSearching={isSearching}
                totalItems={pagination?.total || 0}
                totalPages={pagination?.pages}
                defaultItemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onRowClick={handleRowClick}
                onPageChange={page => {
                  setCurrentPage(page);
                }}
                onItemsPerPageChange={limit => {
                  setItemsPerPage(limit);
                  setCurrentPage(1);
                }}
                theadClassName="bg-red-50 border-b rounded-t-lg"
                thClassName="px-4 py-3 text-left text-sm font-bold text-red-500 uppercase tracking-wider"
                tdClassName="px-4 py-4 text-sm text-left"
                statusColors={{
                  active: 'bg-green-100 text-green-800',
                  inactive: 'bg-red-100 text-red-800',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Customer Details Popup */}
      {selectedCustomer && (
        <PopupWrapper
          title={
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-4">
                <span className="text-4xl font-semibold">{selectedCustomer.companyName}</span>
                {selectedCustomer.customerStatus && (
                  <div
                    className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm ${formatCustomerStatus(selectedCustomer.customerStatus).className
                      }`}
                  >
                    {formatCustomerStatus(selectedCustomer.customerStatus).icon}
                    <span>{formatCustomerStatus(selectedCustomer.customerStatus).text}</span>
                  </div>
                )}
              </div>
              <div className="mt-1 text-sm text-gray-500">Customer ID: {selectedCustomer.id}</div>
            </div>
          }
          isOpen={customerDetailsPopup.isOpen}
          onClose={customerDetailsPopup.closePopup}
          size="lg"
          logo={<FaBuilding size={53} className="text-blue-600" />}
        >
          <div className="flex flex-col py-6">
            <div className="flex mb-6">
              <div className="w-1/5 flex items-center">
                {/* Company Logo */}
                <div className="w-36 h-36 border rounded-full overflow-hidden flex items-center justify-center bg-gray-50">
                  <img
                    src={selectedCustomer.logo}
                    alt={selectedCustomer.companyName}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              <div className="w-4/5">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    label="Company Name"
                    value={selectedCustomer.companyName || '-'}
                    className="bg-gray-100"
                    disabled
                    icon={<FaBuilding className="w-5 h-5" />}
                  />
                  <InputField
                    label="Owner Name"
                    value={selectedCustomer.ownerName || '-'}
                    className="bg-gray-100"
                    disabled
                    icon={<FaUser className="w-5 h-5" />}
                  />
                  <InputField
                    label="Email"
                    value={selectedCustomer.businessEmail || '-'}
                    className="bg-gray-100"
                    disabled
                    icon={<FaEnvelope className="w-5 h-5" />}
                  />
                  <InputField
                    label="Phone"
                    value={selectedCustomer.phone || '-'}
                    className="bg-gray-100"
                    disabled
                    icon={<FaPhone className="w-5 h-5" />}
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <InputField
                label="Business Sector"
                value={selectedCustomer.customerSector || '-'}
                className="bg-gray-100"
                disabled
                icon={<SlGraph className="w-5 h-5" />}
              />
              <InputField
                label="GST Number"
                value={selectedCustomer.gstNumber || '-'}
                className="bg-gray-100"
                disabled
                icon={<GoHash className="w-5 h-5" />}
              />
              <InputField
                label="PAN Number"
                value={selectedCustomer.panNumber || '-'}
                className="bg-gray-100"
                disabled
                icon={<GoHash className="w-5 h-5" />}
              />
              <InputField
                label="Mobile"
                value={selectedCustomer.mobile || '-'}
                className="bg-gray-100"
                disabled
                icon={<FaPhone className="w-5 h-5" />}
              />
            </div>

            {/* Address Information */}
            {/* <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-3">Address Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Client Address</h4>
                  <p className="text-gray-700">
                    {selectedCustomer.clientAddress?.address || '-'}
                    <br />
                    {selectedCustomer.clientAddress?.city || '-'},{' '}
                    {selectedCustomer.clientAddress?.state || '-'}
                    <br />
                    {selectedCustomer.clientAddress?.country || '-'} -{' '}
                    {selectedCustomer.clientAddress?.pinCode || '-'}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Billing Address</h4>
                  <p className="text-gray-700">
                    {selectedCustomer.billingAddress?.address || '-'}
                    <br />
                    {selectedCustomer.billingAddress?.city || '-'},{' '}
                    {selectedCustomer.billingAddress?.state || '-'}
                    <br />
                    {selectedCustomer.billingAddress?.country || '-'} -{' '}
                    {selectedCustomer.billingAddress?.pinCode || '-'}
                  </p>
                </div>
              </div>
            </div> */}

            {/* Notes Section (if available) */}
            {selectedCustomer.note && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <TbNotes className="mr-2" /> Notes
                </h3>
                <p className="text-gray-700">{selectedCustomer.note}</p>
              </div>
            )}

            {/* Action Button */}
            <div className="mt-6">
              <button
                onClick={() => {
                  customerDetailsPopup.closePopup();
                  handleEdit(selectedCustomer.id);
                }}
                className="w-full px-4 py-4 bg-lime-100 text-lg font-semibold rounded-lg hover:bg-lime-200 transition-colors flex items-center border-2 border-lime-600 justify-center gap-2 text-lime-700"
              >
                <TbEdit />
                Edit Customer
              </button>
            </div>
          </div>
        </PopupWrapper>
      )}
    </ComponentWrapper>
  );
};

export default Customers;
